import { memo, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { useSocket } from '@main/hooks';
import { NotificationTemplate } from '../components/NotificationTemplate';
import MessagesModel from '../model/messages';
import { objToBase64 } from '../utils/conversors';
import { NotificationClientContext } from './NotificationClientContext';

const USER_NAME = 'Notifications';
const LOG_PALETTE = 'background-color: rgb(54 83 20); color: white;';

const { Provider } = NotificationClientContext;

/**
 * Componente cliente de notificaciones vía WebSocket.
 *
 * @component
 * @example
 * ```jsx
 * <NotificationClientProvider
 *   baseUri="wss://notify.example.com"
 *   user={{ id: '123', model: 'User' }}
 *   enabled
 *   onConnected={(ok) => console.log('Socket conectado:', ok)}
 *   onTrigger={() => console.log('Se disparó una notificación')}
 * >
 *   <App />
 * </NotificationClientProvider>
 * ```
 *
 * @typedef {Object} NotificationClientProps
 * @property {React.ReactNode} children — Elementos que se renderizarán dentro del provider.
 * @property {boolean} [autoReconnect=false] — Si debe reconectar automáticamente el socket.
 * @property {string} baseUri — URL base del servidor de notificaciones.
 * @property {{id?: string|number, model?: string}} [user={}] — Información del usuario origen.
 * @property {boolean} [enabled=false] — Habilita o deshabilita la conexión de notificaciones.
 * @property {string} [space=''] — Espacio (namespace) que usará el socket.
 * @property {string} [event='notification'] — Nombre del evento que el servidor emite.
 * @property {string} [originPlatform='platform'] — Identificador de la plataforma de origen.
 * @property {string} [externalDeviceId] — ID de dispositivo externo (si existe).
 * @property {(message: import('../model/messages').default) => void} [externalNotificationApi] — Callback que recibe el modelo de mensaje completo.
 * @property {(isConnected: boolean) => void} [onConnected] — Callback cuando el socket cambia de estado.
 * @property {() => void} [onTrigger] — Callback después de mostrar una notificación.
 *
 * @returns {JSX.Element} Context provider que encapsula la lógica de notificaciones.
 */
const NotificationClient = memo(
  ({
    children,
    autoReconnect = false,
    baseUri,
    user = {},
    enabled = false,
    space = '',
    event = 'notification',
    originPlatform = 'platform',
    externalDeviceId,
    externalNotificationApi,
    onConnected = () => { },
    onTrigger = () => { },
  }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [deviceId, setDeviceId] = useState(undefined);
    const [originId, setOriginId] = useState(undefined);
    const [originModel, setOriginModel] = useState(undefined);

    const onClickNotificationRef = useRef(null);

    const subscribeOnClick = (callback) => {
      onClickNotificationRef.current = callback;
    };

    useEffect(() => {
      if (user.id === undefined) return;
      setOriginId(user.id);
    }, [user?.id]);

    useEffect(() => {
      if (user.model === undefined) return;
      setOriginModel(user.model);
    }, [user?.model]);

    const {
      listenEvent: notifyListenEvent,
      socketState: notifySocketState,
      updateUrl: notifyUpdateUrl,
      updateQuery: notifyUpdateQuery,
      tryConnect: notifyTryConnect,
      tryDisconnect: notifyTryDisconnect,
      socketStatus,
    } = useSocket(baseUri, space, {
      name: USER_NAME,
      autoReconnect,
      styleLog: LOG_PALETTE,
    });

    useEffect(() => {
      if (notifySocketState === undefined) return;
      if (notifySocketState === socketStatus.CONNECTED) {
        onConnected(true);
      } else {
        onConnected(false);
      }
    }, [notifySocketState]);

    // -------- Handlers ----------
    const handleSetNotification = (payload) => {
      if (payload === undefined) return;
      const filteredItem = _.omit(payload, [
        'targetModel',
        'targetId',
        'targetPlatform',
        'updated_at',
        'deleted_at',
      ]);
      const item = MessagesModel(filteredItem);
      if (externalNotificationApi && typeof externalNotificationApi === 'function') {
        externalNotificationApi(item);
      }
      enqueueSnackbar(item.title, {
        key: item.id,
        autoHideDuration: '8000',
        content: () => (
          <NotificationTemplate
            item={item}
            onClose={() => {
              closeSnackbar(item.id);
            }}
            onClick={(result) => {
              if (onClickNotificationRef.current) {
                onClickNotificationRef.current(result);
              }
            }}
          />
        ),
      });
      const timeNotify = setTimeout(() => {
        onTrigger();
        clearTimeout(timeNotify);
      }, 800);
    };

    // -------- Watchers ----------
    useEffect(() => {
      if (externalDeviceId === undefined) {
        const newId = uuidv4();
        setDeviceId(newId);
      } else {
        setDeviceId(externalDeviceId);
      }
    }, [externalDeviceId]);

    useEffect(() => {
      notifyTryDisconnect();
    }, []);

    // -------- Si setup autoriza uso de App se inicializa socket
    useEffect(() => {
      if (!enabled) {
        notifyTryDisconnect();
      } else {
        notifyTryConnect();
      }
    }, [enabled]);

    // ------ Actualizacion de parametros por Query para autorizacion de conexion con Notify
    useEffect(() => {
      if (
        originId === undefined ||
        originModel === undefined ||
        originPlatform === undefined ||
        deviceId === undefined ||
        !enabled
      )
        return;
      const tokenLogStr = `[Notification Client | Token]\noriginId: ${originId}\noriginModel: ${originModel}\noriginPlatform: ${originPlatform}\ndeviceId: ${deviceId}`;
      const rawToken = {
        originId,
        originModel,
        originPlatform,
        deviceId,
      };
      const token = objToBase64(rawToken);
      notifyUpdateQuery({ token })
        .then((resQuery) => {
          // Nothing to do
        })
        .catch((err) => console.log(err));
    }, [enabled, originId, originModel, originPlatform, deviceId]);

    // ------ Actualizacion de referencia de url de conexion para socket Notify acorde archivo de configuracion envpaths.
    useEffect(() => {
      if (baseUri === undefined || !enabled) return;
      notifyUpdateUrl(`${baseUri}`)
        .then((resUri) => {
          // Nothing to do
        })
        .catch((err) => console.log(err));
    }, [baseUri, enabled]);

    // -------- Inicializando Listeners ON ---
    useEffect(() => {
      if (event === undefined) return;
      notifyListenEvent(event, (res) => {
        console.log(res);
        if (res === undefined || res === null) return;
        handleSetNotification(res);
      });
    }, [event]);

    return <Provider value={{ notifyLicenseState: notifySocketState, subscribeOnClick }}>{children}</Provider>;
  }
);

/**
 * @typedef {import('./NotificationClient').NotificationClientProps} NotificationClientProps
 */

/**
 * Provider que envuelve a {@link NotificationClient} con el
 * context de **notistack** (`SnackbarProvider`).
 *
 * Hereda **todas** las props de `NotificationClient`.
 *
 * @function NotificationClientProvider
 * @see NotificationClient
 *
 * @param {NotificationClientProps} props — Props heredadas de `NotificationClient`.
 * @returns {JSX.Element} Componente provider listo para usar.
 */
const NotificationClientProvider = ({ ...props }) => {
  return (
    <SnackbarProvider
      maxSnack={5}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      classes={{
        containerRoot: 'bottom-0 right-0 mb-52 md:mb-68 mr-8 lg:mr-80 z-99',
      }}
    >
      <NotificationClient {...props} />
    </SnackbarProvider>
  );
};

export default NotificationClientProvider;
